/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { TipBody, useHoverTip } from "@/components/common/HoverTip";
import { useMediaQuery } from "@/hooks/useMediaQuery";

// ---- formatting ----

export function fmtBytes(n: number | undefined | null, digits = 1): string {
  if (n === undefined || n === null || !isFinite(n)) return "-";
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ["KiB", "MiB", "GiB", "TiB", "PiB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : digits)} ${units[i]}`;
}

export function fmtRate(bytesPerSec: number | undefined | null): string {
  if (bytesPerSec === undefined || bytesPerSec === null || !isFinite(bytesPerSec)) return "-";
  if (bytesPerSec < 1024) return `${Math.round(bytesPerSec)} B/s`;
  return `${fmtBytes(bytesPerSec)}/s`;
}

export function fmtBits(bytesPerSec: number): string {
  const bits = bytesPerSec * 8;
  if (bits < 1000) return `${Math.round(bits)} bit/s`;
  const units = ["kbit/s", "Mbit/s", "Gbit/s"];
  let v = bits / 1000;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

export function fmtDuration(seconds: number | undefined | null): string {
  if (seconds === undefined || seconds === null || !isFinite(seconds) || seconds < 0) return "-";
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

export function fmtPct(n: number | undefined | null, digits = 0): string {
  if (n === undefined || n === null || !isFinite(n)) return "-";
  return `${n.toFixed(digits)}%`;
}

export type Level = "" | "warn" | "crit";

/** Bar/number colour level for a utilisation percentage. */
export function level(pct: number, warn = 75, crit = 90): Level {
  if (pct >= crit) return "crit";
  if (pct >= warn) return "warn";
  return "";
}

export function levelClass(l: Level): string {
  return l === "crit" ? "sv-crit" : l === "warn" ? "sv-warn" : "";
}

// ---- primitives ----

export const Panel: React.FC<{
  title: string;
  hint?: React.ReactNode;
  className?: string;
  /** Open by default on mobile. Ignored on desktop, where panels never collapse. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, hint, className = "sv-c6", defaultOpen = false, children }) => {
  // Below the servers-grid breakpoint (720px) the board is a single endless
  // column, so each panel becomes a tappable accordion to tame the scroll.
  // On desktop the head is inert and the body is always shown.
  const collapsible = useMediaQuery("(max-width: 720px)");
  const [open, setOpen] = React.useState(defaultOpen);
  const expanded = !collapsible || open;
  const toggle = () => setOpen((o) => !o);

  return (
    <section
      className={`sv-panel ${className}${collapsible ? " sv-panel--collapsible" : ""}${
        collapsible && !expanded ? " sv-panel--closed" : ""
      }`}
    >
      <div
        className="sv-panel-head"
        {...(collapsible
          ? {
              role: "button" as const,
              tabIndex: 0,
              "aria-expanded": expanded,
              onClick: toggle,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle();
                }
              },
            }
          : {})}
      >
        <span className="gh-label">{title}</span>
        <span className="sv-panel-head-r">
          {hint !== undefined && hint !== null && <span className="gh-sub">{hint}</span>}
          {collapsible && (
            <svg className="sv-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      </div>
      {expanded && children}
    </section>
  );
};

export const Bar: React.FC<{ pct: number; warn?: number; crit?: number }> = ({ pct, warn, crit }) => {
  const p = Math.max(0, Math.min(100, isFinite(pct) ? pct : 0));
  const l = level(p, warn, crit);
  return (
    <div className="gh-bar" role="progressbar" aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100}>
      <i className={l ? `gh-bar--${l}` : ""} style={{ width: `${p}%` }} />
    </div>
  );
};

export const Meter: React.FC<{
  label: string;
  value: string;
  pct: number;
  sub?: string;
  warn?: number;
  crit?: number;
}> = ({ label, value, pct, sub, warn, crit }) => (
  <div className="sv-meter">
    <div className="sv-meter-top">
      <span>{label}</span>
      <span>{value}</span>
    </div>
    <Bar pct={pct} warn={warn} crit={crit} />
    {sub && <div className="sv-meter-sub">{sub}</div>}
  </div>
);

export const KV: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className="sv-kv-row">
    <span>{k}</span>
    <span>{children}</span>
  </div>
);

export const Tag: React.FC<{ tone?: "ok" | "warn" | "crit" | ""; children: React.ReactNode }> = ({
  tone = "",
  children,
}) => <span className={`sv-tag${tone ? ` sv-tag--${tone}` : ""}`}>{children}</span>;

export const StatusDot: React.FC<{ state: "up" | "warn" | "down" | "idle"; pulse?: boolean }> = ({
  state,
  pulse,
}) => <span className={`gh-dot gh-dot--${state}${pulse ? " sv-pulse" : ""}`} />;

export interface SparkSeries {
  values: number[];
  color: string; // any CSS colour / var()
  label?: string;
}

/** Tiny multi-series area/line chart; all series share one y-scale. Hover (or touch) shows the values at that point. */
export const Spark: React.FC<{
  series: SparkSeries[];
  max?: number;
  height?: number;
  legend?: boolean;
  format?: (v: number) => string;
  /** One heading per sample for the hover tooltip (a time…), aligned with the longest series. */
  heads?: React.ReactNode[];
}> = ({ series, max, height = 54, legend = true, format, heads }) => {
  const W = 300;
  const H = height;
  const len = Math.max(...series.map((s) => s.values.length), 0);
  const peak = max ?? Math.max(1, ...series.flatMap((s) => s.values));
  const x = (i: number) => (len <= 1 ? W : (i / (len - 1)) * W);
  const y = (v: number) => H - 2 - (Math.min(v, peak) / peak) * (H - 4);
  const tip = useHoverTip();
  const [hover, setHover] = React.useState<number | null>(null);
  const fmt = format ?? ((v: number) => `${+v.toFixed(2)}`);
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (len < 2) return;
    const r = e.currentTarget.getBoundingClientRect();
    const t = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const i = Math.round(t * (len - 1));
    setHover(i);
    tip.show(
      e.clientX,
      e.clientY,
      <TipBody
        head={heads?.[i]}
        rows={series.flatMap((s) => {
          const v = s.values[i - (len - s.values.length)];
          return v === undefined ? [] : [{ color: s.color, name: s.label, value: fmt(v) }];
        })}
      />,
    );
  };
  const onLeave = () => {
    setHover(null);
    tip.hide();
  };
  return (
    <div>
      <div className="relative" onPointerMove={onMove} onPointerDown={onMove} onPointerLeave={onLeave} onPointerCancel={onLeave}>
        <svg className="sv-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height }}>
          <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="var(--gh-border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          {len > 1 &&
            series.map((s, si) => {
              if (s.values.length < 2) return null;
              const off = len - s.values.length;
              const pts = s.values.map((v, i) => `${x(i + off).toFixed(1)},${y(v).toFixed(1)}`);
              return (
                <g key={si}>
                  <polygon
                    points={`${x(off).toFixed(1)},${H} ${pts.join(" ")} ${x(len - 1).toFixed(1)},${H}`}
                    fill={s.color}
                    opacity="0.12"
                  />
                  <polyline
                    points={pts.join(" ")}
                    fill="none"
                    stroke={s.color}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              );
            })}
          {hover !== null && (
            <line x1={x(hover)} x2={x(hover)} y1="0" y2={H} stroke="var(--gh-mute)" strokeOpacity="0.6" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
          )}
        </svg>
        {hover !== null &&
          series.map((s, si) => {
            const v = s.values[hover - (len - s.values.length)];
            return v === undefined ? null : (
              <i key={si} className="gh-dot-mark" style={{ left: `${(x(hover) / W) * 100}%`, top: y(v), background: s.color }} />
            );
          })}
        {tip.node}
      </div>
      {legend && (
        <div className="sv-spark-legend">
          {series.map((s, i) => (
            <span key={i}>
              <i style={{ background: s.color }} />
              {s.label}
              {format && s.values.length > 0 ? ` ${format(s.values[s.values.length - 1])}` : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
